// Avatar de un profesional: sus iniciales sobre el color con el que se lo
// distingue en la agenda.
//
// Existe porque el color solo no alcanza. En la vista semana, con dos o tres
// personas atendiendo a la vez, "el bloque azul" obliga a acordarse de quién
// es azul; "JP sobre azul" no.
//
// Está preparado para fotos aunque todavía no haya dónde guardarlas: cuando
// exista el almacenamiento, se le pasa `foto` y muestra la imagen en lugar de
// las iniciales, sin tocar ninguno de los lugares que lo usan.

const NEGRO = '#0A0A0B';
const BLANCO = '#FFFFFF';

/** '#25D366' -> {r, g, b}. Acepta con y sin '#', de 3 o 6 dígitos. */
function aRgb(hex) {
  const limpio = String(hex ?? '').replace('#', '').trim();
  const largo = limpio.length === 3
    ? limpio.split('').map((c) => c + c).join('')
    : limpio;

  if (!/^[0-9a-fA-F]{6}$/.test(largo)) return null;

  return {
    r: parseInt(largo.slice(0, 2), 16),
    g: parseInt(largo.slice(2, 4), 16),
    b: parseInt(largo.slice(4, 6), 16)
  };
}

/** Luminancia relativa según WCAG 2.1. */
function luminancia({ r, g, b }) {
  const canal = (valor) => {
    const s = valor / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b);
}

const contraste = (a, b) => {
  const [claro, oscuro] = a > b ? [a, b] : [b, a];
  return (claro + 0.05) / (oscuro + 0.05);
};

/**
 * Negro o blanco, el que más contraste dé contra ese fondo.
 *
 * Se calcula en vez de fijar uno: los colores que elige cada negocio son suyos
 * y un blanco sobre amarillo no se lee. Con el umbral fijo del "si es claro,
 * texto oscuro" los tonos del medio quedan siempre justos.
 */
export function textoQueContrasta(color) {
  const rgb = aRgb(color);
  if (!rgb) return BLANCO;

  const fondo = luminancia(rgb);
  return contraste(fondo, luminancia(aRgb(NEGRO))) >= contraste(fondo, luminancia(aRgb(BLANCO)))
    ? NEGRO
    : BLANCO;
}

/**
 * Las iniciales que se muestran.
 *
 * Un nombre solo da una letra ('Nico' -> 'N') y dos o más dan dos
 * ('Juan Pérez' -> 'JP'). Cortar 'Nico' en 'NI' se lee como una sigla rara.
 */
export function inicialesDe(nombre) {
  const palabras = String(nombre ?? '').trim().split(/\s+/).filter(Boolean);
  if (palabras.length === 0) return '?';
  if (palabras.length === 1) return palabras[0][0].toUpperCase();
  return (palabras[0][0] + palabras[1][0]).toUpperCase();
}

/**
 * @param {object} props
 * @param {string} props.nombre
 * @param {string} [props.color]   color_agenda del profesional
 * @param {string} [props.foto]    URL; cuando exista, reemplaza a las iniciales
 * @param {number} [props.tamano]  lado del círculo, en píxeles
 */
export function Avatar({ nombre, color = '#25D366', foto, tamano = 36, className = '' }) {
  const estilo = {
    '--av': `${tamano}px`,
    background: color,
    color: textoQueContrasta(color)
  };

  return (
    <span
      className={`avatar ${className}`.trim()}
      style={estilo}
      // El avatar siempre acompaña al nombre escrito, así que para un lector
      // de pantalla es decoración: leerlo sería decir el nombre dos veces.
      aria-hidden="true"
    >
      {foto
        ? <img src={foto} alt="" className="avatar-foto" />
        : inicialesDe(nombre)}
    </span>
  );
}
